'use client'

import { useState } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { Checkbox } from '@/app/components/atoms/forms/Checkbox'
import type { RangeSliderValue } from '@/app/components/shared/forms/RangeSliderField'
import { ListingFilters, toSelectOptions } from '@/app/components/shared/ListingFilters'
import { PAGE_PARAM } from '@/app/lib/constants'
import { useListingFieldFilters } from '@/app/lib/hooks/useListingFieldFilters'

import { READABLE_FILTER_PARAMS } from '@/app/entradas/(general-web)/recintos/[location]/[url]/_components/constants'

import { buildApplyHref } from './recintoEventsFilterHrefs'
import type { RecintoEventsFiltersProps } from './RecintoEventsFilters.types'

/**
 * Resuelve qué extremos del rango de Precio viajan a la URL legible: si el
 * rango seleccionado coincide con el rango COMPLETO de `price_range` (el
 * usuario no ha tocado el slider), se omiten los dos — equivalente a "sin
 * filtro". Mismo criterio que `PromocionesFilters`/`LoMasVendidoFilters`/
 * `SubcategoriaFilters`.
 */
function resolvePriceParams(
  range: RangeSliderValue,
  bounds: RangeSliderValue,
): { min: number | undefined; max: number | undefined } {
  const isFullRange = range.min === bounds.min && range.max === bounds.max
  return isFullRange ? { min: undefined, max: undefined } : { min: range.min, max: range.max }
}

/**
 * `RecintoEventsFilters` — envoltorio fino sobre `ListingFilters` (DS
 * compartido) para el listado de eventos de un recinto. Orden exigido:
 * "Ordenar por" → "Categoría" → "Precio" (research.md §4), con chips de
 * fecha y cambio de vista rejilla/lista, igual que el resto de PLPs del
 * sitio.
 *
 * A diferencia de `SubcategoriaFilters`, ningún campo navega un segmento de
 * ruta (el recinto ya tiene una ubicación fija) — los selectores son query
 * params puros, gestionados con `useListingFieldFilters`. En desktop cada
 * campo se aplica al instante vía su propio `onChange`; en el SideSheet
 * móvil se acumulan en el borrador y se aplican juntos con "Aplicar filtros
 * (N)" (`onApply`).
 *
 * Precio (revisado — mismo mecanismo que `PromocionesFilters`/
 * `LoMasVendidoFilters`/`SubcategoriaFilters`, unifica la maquetación del
 * selector de precio en todas las PLP del sitio salvo `/search`) pasó de un
 * `min_price` de tramo único (`fields[]`) a un rango CONTINUO acotado por
 * `filters.price_range`, modelado como el `rangeField` de `ListingFilters`
 * (slider de doble asa), de aplicación instantánea al soltar el slider,
 * fuera del borrador. Junto al slider, un checkbox "Mostrar solo gratuitos"
 * (`extra`) — visible solo si la clave `filters.free_filter` está presente
 * en la respuesta — navega con `free_filter=on` y deshabilita el slider
 * (`rangeField.sliderDisabled`, el propio `extra` sigue alcanzable para
 * poder desmarcarlo).
 */
export function RecintoEventsFilters({ filters, totalResults }: RecintoEventsFiltersProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { values, setFilter, setFilters } = useListingFieldFilters({
    paramNames: READABLE_FILTER_PARAMS,
    pageParam: PAGE_PARAM,
  })
  // Recuerda el rango previo a marcar "Mostrar solo gratuitos", para
  // restaurarlo al desmarcar — mismo patrón que `PromocionesFilters`.
  const [rangeBeforeFreeOnly, setRangeBeforeFreeOnly] = useState<RangeSliderValue | null>(null)

  const isPriceFilterActive = values.priceMin !== '' || values.priceMax !== ''
  const isFreeFilterActive = values.freeFilter === 'on'
  const showFreeFilterCheckbox = Boolean(filters.free_filter)
  // Mismo criterio que `PromocionesFilters`: los límites del slider se
  // congelan en cuanto el filtro de precio se aplica. `isFreeFilterActive`
  // cuenta igual que `isPriceFilterActive`: con "Mostrar solo gratuitos"
  // activo el backend acota `price_range` a `[0, 0]`, y dejarlo pasar clava
  // los límites reales del slider en cero (bug detectado 2026-09-09: los
  // thumbs se juntan al desmarcar y no se pueden separar).
  const [stableBounds, setStableBounds] = useState<RangeSliderValue>(filters.price_range)
  if (
    !isPriceFilterActive &&
    !isFreeFilterActive &&
    (stableBounds.min !== filters.price_range.min || stableBounds.max !== filters.price_range.max)
  ) {
    setStableBounds(filters.price_range)
  }
  const priceBounds = stableBounds
  // Con "Mostrar solo gratuitos" activo la URL no lleva `price_min`/
  // `price_max` (`resolvePriceParams` los omite al coincidir con el rango
  // completo), solo `free_filter=on` — sin este caso especial,
  // `currentPriceRange` caería al `else` y devolvería `priceBounds` (rango
  // completo) en vez de `[0, 0]`, indistinguible de "sin filtro" para
  // `rangeField` (bug detectado 2026-09-11: ni el chip de filtro aplicado ni
  // el texto del desplegable, "Gratuitos", podían reaccionar).
  const currentPriceRange: RangeSliderValue = isFreeFilterActive
    ? { min: 0, max: 0 }
    : {
        min: values.priceMin !== '' ? Number(values.priceMin) : priceBounds.min,
        max: values.priceMax !== '' ? Number(values.priceMax) : priceBounds.max,
      }

  function handleApply(applied: readonly string[]) {
    router.push(buildApplyHref(pathname, applied, searchParams.toString()), { scroll: false })
  }

  function handlePriceChangeEnd(range: RangeSliderValue) {
    const { min, max } = resolvePriceParams(range, priceBounds)
    setFilters({
      priceMin: min !== undefined ? String(min) : '',
      priceMax: max !== undefined ? String(max) : '',
      freeFilter: '',
    })
  }

  function handleFreeFilterChange(checked: boolean) {
    if (checked) {
      setRangeBeforeFreeOnly(currentPriceRange)
      setFilters({ priceMin: '', priceMax: '', freeFilter: 'on' })
      return
    }
    const restoredRange = rangeBeforeFreeOnly ?? priceBounds
    const { min, max } = resolvePriceParams(restoredRange, priceBounds)
    setFilters({
      priceMin: min !== undefined ? String(min) : '',
      priceMax: max !== undefined ? String(max) : '',
      freeFilter: '',
    })
  }

  function clearPriceRange() {
    // `rangeField.onClear` — lo invoca "Limpiar filtros" del SideSheet (el
    // precio vive fuera del borrador y navega ya, ver comentario de arriba).
    setRangeBeforeFreeOnly(null)
    setFilters({ priceMin: '', priceMax: '', freeFilter: '' })
  }

  return (
    <ListingFilters
      dateField={{
        label: 'Fecha',
        value: values.dateRange,
        options: toSelectOptions(filters.date_range.items),
        onChange: (value) => setFilter('dateRange', value),
      }}
      fields={[
        {
          label: 'Ordenar por',
          value: values.sort,
          options: toSelectOptions(filters.sort.items),
          placeholder: 'Ordenar por',
          disabled: filters.sort.items.length === 0,
          // El orden no es un filtro que se "quite": no genera chip.
          chip: false,
          onChange: (value) => setFilter('sort', value),
        },
        {
          label: 'Categoría',
          value: values.genre,
          options: toSelectOptions(filters.genre.items),
          placeholder: 'Todas las categorías',
          disabled: filters.genre.items.length === 0,
          onChange: (value) => setFilter('genre', value),
        },
      ]}
      rangeField={{
        label: 'Precio',
        min: priceBounds.min,
        max: priceBounds.max,
        value: currentPriceRange,
        sliderDisabled: isFreeFilterActive,
        onChangeEnd: handlePriceChangeEnd,
        onClear: clearPriceRange,
        extra: showFreeFilterCheckbox
          ? ({ variant }) => (
              <Checkbox
                label="Mostrar solo gratuitos"
                checked={isFreeFilterActive}
                onChange={handleFreeFilterChange}
                inverse={variant === 'inverse'}
              />
            )
          : undefined,
      }}
      totalResults={totalResults}
      clearValues={['', '']}
      onApply={handleApply}
      showViewToggle
    />
  )
}

